import * as React from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, Navigate, useParams } from 'react-router'
import { ArrowLeft, Download, Loader2 } from 'lucide-react'
import type { AuditEventDto, EnvelopeCreatorDto, EnvelopeDto } from '@lib/dto/envelope.dto'
import { uuidParamSchema, type Uuid } from '@lib/validators/params'
import { ExtendDeadlineDialog } from '@/components/envelopes/ExtendDeadlineDialog'
import { EnvelopeStatusBadge, SignerStatusBadge } from '@/components/envelopes/StatusBadge'
import { PageHeader } from '@/components/PageHeader'
import { RecordFacts } from '@/components/RecordSheet'
import { ErrorState, LoadingState } from '@/components/PageState'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { toast } from '@/components/ui/toast'
import { apiErrorMessage } from '@/helpers/apiErrorMessage'
import { downloadBlob } from '@/helpers/download'
import { useEnvelopeAccess, type EnvelopeAccessValue } from '@/envelopes/EnvelopeAccess'
import { envelopeKeys } from '@/hooks/queryKeys'
import { useEnvelope, useEnvelopeAudit } from '@/hooks/useEnvelope'
import { useEnvelopeQuestions } from '@/hooks/useEnvelopeQuestions'

/** The envelope an action is for, and the cache and service it was started with. */
interface ActionTarget {
  id: Uuid
  cacheKey: EnvelopeAccessValue['cacheKey']
  service: EnvelopeAccessValue['service']
}

function RetryButton({ onClick }: { onClick: () => void }) {
  return (
    <Button variant="secondary" size="sm" onClick={onClick}>
      Try again
    </Button>
  )
}

function download(fetchFile: () => Promise<Blob>, filename: string, onDone: () => void): void {
  void fetchFile()
    .then((blob) => downloadBlob(blob, filename))
    .then(onDone)
    .catch((error) => toast.error(apiErrorMessage(error, `Could not download ${filename}.`)))
}

const ACTOR_LABELS: Record<AuditEventDto['actorType'], string> = {
  API_KEY: 'API key',
  SIGNER: 'Signer',
  STAFF: 'PixelSign staff',
  SYSTEM: 'PixelSign',
  TENANT_USER: 'Dashboard user',
}

interface EnvelopeProps {
  envelope: EnvelopeDto
}

interface DocumentsCardProps extends EnvelopeProps {
  access: EnvelopeAccessValue
}

/** Whether a signer is waiting on their turn (pending or viewed). */
function isActive(signer: EnvelopeDto['signers'][number]): boolean {
  return signer.status === 'pending' || signer.status === 'viewed'
}

/**
 * What the Actions card says about reminders. Signers whose link comes from
 * the organization's app (`delivery: caller`) are never reminded, so an
 * envelope where every active signer is one has nobody to remind.
 */
function remindNote(envelope: EnvelopeDto): { canRemind: boolean; note: string } {
  const active = envelope.signers.filter(isActive)
  const emailed = active.filter((signer) => signer.delivery === 'email')
  if (emailed.length === 0) {
    return { canRemind: false, note: 'The active signers get their link from the organization’s app, so PixelSign doesn’t remind them.' }
  }
  if (emailed.length < active.length) {
    return { canRemind: true, note: 'Reminders are capped at 3 per signer per day. Signers whose link comes from the organization’s app are skipped.' }
  }
  return { canRemind: true, note: 'Reminders are capped at 3 per signer per day.' }
}

function SignersTable({ envelope }: EnvelopeProps) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Signer</TableHead>
          <TableHead>Role</TableHead>
          <TableHead className="text-right">Order</TableHead>
          <TableHead>Status</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {envelope.signers.map((signer) => (
          <TableRow key={signer.id}>
            <TableCell>
              <p className="font-medium">{signer.name}</p>
              <p className="text-caption text-muted-foreground">
                {signer.email}
                {signer.questionsEnabled && signer.questionsAnswerer
                  ? ` · questions to ${signer.questionsAnswerer.name}`
                  : ''}
                {signer.delivery === 'caller' ? ' · link from the organization’s app, not emailed' : ''}
                {signer.expiresAt ? ` · due ${new Date(signer.expiresAt).toLocaleString()}` : ''}
              </p>
              {signer.paperCopyRequestedAt ? (
                <p className="text-caption text-muted-foreground">
                  Asked for a paper copy {new Date(signer.paperCopyRequestedAt).toLocaleString()}
                  {signer.paperCopyNote ? `: “${signer.paperCopyNote}”` : ''}. Mail it to them.
                </p>
              ) : null}
              {signer.declinedConsent ? (
                <p className="text-caption text-muted-foreground">
                  Declined on the consent screen, before agreeing to sign electronically: offer them paper.
                </p>
              ) : null}
            </TableCell>
            <TableCell className="text-muted-foreground">{signer.role}</TableCell>
            <TableCell className="text-right">{signer.routingOrder}</TableCell>
            <TableCell>
              <div className="flex flex-wrap gap-1">
                <SignerStatusBadge status={signer.status} />
                {signer.paperCopyRequestedAt ? <Badge tone="warning">Paper copy requested</Badge> : null}
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

/** Who created the envelope: the API key (and the AI app, when used from one), or a person over an AI app. */
function CreatorValue({ creator }: { creator: EnvelopeCreatorDto | undefined }) {
  if (!creator) return <span className="text-muted-foreground">—</span>
  if (creator.person) {
    return (
      <span>
        {creator.person.name} via {creator.app ?? 'an AI app'}
        {creator.person.email ? <span className="block text-muted-foreground">{creator.person.email}</span> : null}
      </span>
    )
  }
  if (creator.apiKey) {
    return (
      <span>
        API key {creator.apiKey.name}
        {creator.app ? ` via ${creator.app}` : null}
        <span className="block font-mono text-muted-foreground">{creator.apiKey.prefix}…</span>
      </span>
    )
  }
  return <span>API key</span>
}

function DetailsCard({ envelope }: { envelope: EnvelopeDto }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Details</CardTitle>
      </CardHeader>
      <CardContent>
        <RecordFacts>
          <dt className="text-muted-foreground">Created</dt>
          <dd>{new Date(envelope.createdAt).toLocaleString()}</dd>
          <dt className="text-muted-foreground">Created by</dt>
          <dd className="min-w-0 break-words">
            <CreatorValue creator={envelope.createdBy} />
          </dd>
          {envelope.status === 'draft' || envelope.status === 'pending' ? (
            <>
              <dt className="text-muted-foreground">Expires</dt>
              <dd>{new Date(envelope.expiresAt).toLocaleString()}</dd>
            </>
          ) : null}
        </RecordFacts>
      </CardContent>
    </Card>
  )
}

function DocumentsCard({ envelope, access }: DocumentsCardProps) {
  const completed = envelope.status === 'completed'
  const { service } = access
  const queryClient = useQueryClient()
  // Downloads are audited; show the new entry.
  const refreshAudit = (): void => {
    void queryClient.invalidateQueries({ queryKey: envelopeKeys.audit(access.cacheKey, envelope.id) })
  }
  return (
    <Card>
      <CardHeader>
        <CardTitle>Files</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-1">
        {envelope.documents.map((document) => (
          <Button
            key={document.id}
            variant="ghost"
            size="sm"
            className="justify-start"
            onClick={() => download(() => service.downloadDocument(envelope.id, document.id), document.filename, refreshAudit)}
          >
            <Download />
            {document.title}
          </Button>
        ))}
        <Button
          variant="ghost"
          size="sm"
          className="justify-start"
          onClick={() => download(() => service.downloadUnsignedPacket(envelope.id), 'unsigned-packet.pdf', refreshAudit)}
        >
          <Download />
          Unsigned packet
        </Button>
        {completed ? (
          <>
            <Button
              variant="ghost"
              size="sm"
              className="justify-start"
              onClick={() => download(() => service.downloadPacket(envelope.id), 'signed-packet.pdf', refreshAudit)}
            >
              <Download />
              Signed packet
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="justify-start"
              onClick={() => download(() => service.downloadCertificate(envelope.id), 'certificate.pdf', refreshAudit)}
            >
              <Download />
              Certificate
            </Button>
          </>
        ) : null}
      </CardContent>
    </Card>
  )
}

/**
 * One envelope: who signs, files, questions, actions, and the audit trail.
 * Serves the ops console and tenant dashboards (see `EnvelopeAccess`); what
 * the viewer may do comes from there.
 */
export function EnvelopeDetailPage() {
  const { id: rawId } = useParams()
  const parsedId = uuidParamSchema.safeParse(rawId)
  const id = parsedId.success ? parsedId.data : null
  const access = useEnvelopeAccess()
  // An envelope belongs to the mode and organization it was opened in. When
  // the viewer switches either, stop asking for it there and go to the list.
  const [openedIn] = React.useState(access.cacheKey)
  const stillHere = access.cacheKey === openedIn
  const queryClient = useQueryClient()
  const envelopeId = stillHere && id !== null ? id : undefined
  const envelopeQuery = useEnvelope(envelopeId)
  const auditQuery = useEnvelopeAudit(envelopeId)
  const questionsEnabled = Boolean(
    envelopeQuery.data &&
      (envelopeQuery.data.answerers.length > 0 ||
        envelopeQuery.data.signers.some((signer) => signer.questionsEnabled)),
  )
  const questionsQuery = useEnvelopeQuestions(id ?? undefined, questionsEnabled && stillHere)
  const [extendOpen, setExtendOpen] = React.useState(false)
  const [voidOpen, setVoidOpen] = React.useState(false)
  const [voidReason, setVoidReason] = React.useState('')
  const [actionError, setActionError] = React.useState<string | null>(null)

  // Each action carries the envelope and cache it was started for, so a result
  // that settles after the viewer has opened another envelope lands on its own.
  const remind = useMutation({
    mutationFn: (target: ActionTarget) => target.service.remind(target.id, {}),
    onSuccess: (data, target) => {
      queryClient.setQueryData(envelopeKeys.detail(target.cacheKey, target.id), data)
      void queryClient.invalidateQueries({ queryKey: envelopeKeys.audit(target.cacheKey, target.id) })
      setActionError(null)
      toast.success('Reminder sent to active signers')
    },
    onError: (error) => setActionError(apiErrorMessage(error)),
  })
  const voidEnvelope = useMutation({
    mutationFn: ({ reason, ...target }: ActionTarget & { reason: string }) => target.service.voidEnvelope(target.id, { reason }),
    onSuccess: (data, target) => {
      queryClient.setQueryData(envelopeKeys.detail(target.cacheKey, target.id), data)
      void queryClient.invalidateQueries({ queryKey: envelopeKeys.audit(target.cacheKey, target.id) })
      void queryClient.invalidateQueries({ queryKey: [...envelopeKeys.scope(target.cacheKey), 'list'] })
      setVoidOpen(false)
      setActionError(null)
      toast.success('Envelope voided')
    },
  })

  const backLink = (
    <Link to={access.listPath} className="inline-flex items-center gap-1 hover:text-foreground">
      <ArrowLeft className="size-3.5" />
      Envelopes
    </Link>
  )

  if (!stillHere) return <Navigate to={access.listPath} replace />
  if (id === null) {
    return (
      <>
        <PageHeader eyebrow={backLink} title="Envelope" />
        <ErrorState message="That envelope link is not valid." />
      </>
    )
  }
  if (envelopeQuery.isLoading) return <LoadingState />
  if (envelopeQuery.isError || !envelopeQuery.data) {
    return (
      <>
        <PageHeader eyebrow={backLink} title="Envelope" />
        <ErrorState message={apiErrorMessage(envelopeQuery.error, 'Envelope not found.')} />
      </>
    )
  }

  const envelope = envelopeQuery.data
  const actionTarget: ActionTarget = { id: envelope.id, cacheKey: access.cacheKey, service: access.service }
  const pending = envelope.status === 'pending'
  const reminders = remindNote(envelope)
  return (
    <>
      <PageHeader
        eyebrow={backLink}
        title={envelope.title}
        description={<span className="font-mono text-caption">{envelope.id}</span>}
        actions={
          <>
            {access.webhooksPath ? (
              <Button variant="secondary" size="sm" asChild>
                <Link to={access.webhooksPath(envelope.id)}>Webhooks</Link>
              </Button>
            ) : null}
            {access.mailboxPath ? (
              <Button variant="secondary" size="sm" asChild>
                <Link to={access.mailboxPath(envelope.id)}>Emails</Link>
              </Button>
            ) : null}
            {envelope.mode === 'test' ? <Badge tone="warning">Test</Badge> : null}
            <EnvelopeStatusBadge status={envelope.status} />
          </>
        }
      />
      {access.notice}
      {actionError ? <ErrorState message={actionError} className="mb-6" /> : null}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_18rem]">
        <div className="min-w-0 space-y-8">
          <section className="space-y-3">
            <h2 className="text-title font-semibold">Signers</h2>
            <SignersTable envelope={envelope} />
          </section>

          {envelope.answerers.length > 0 ? (
            <section className="space-y-3">
              <h2 className="text-title font-semibold">Answerers</h2>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Answerer</TableHead>
                    <TableHead>Relationship</TableHead>
                    <TableHead>Consent</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {envelope.answerers.map((answerer) => (
                    <TableRow key={answerer.id}>
                      <TableCell>
                        <p className="font-medium">{answerer.name}</p>
                        <p className="text-caption text-muted-foreground">{answerer.email}</p>
                      </TableCell>
                      <TableCell className="text-muted-foreground">{answerer.relationship}</TableCell>
                      <TableCell>
                        {answerer.consentAcceptedAt ? (
                          <Badge tone="success">Accepted</Badge>
                        ) : (
                          <Badge tone="neutral">Pending</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </section>
          ) : null}

          {questionsEnabled ? (
            <section className="space-y-3">
              <div>
                <h2 className="text-title font-semibold">Questions</h2>
                <p className="text-body text-muted-foreground">
                  {access.surface === 'ops'
                    ? 'Each thread is only between that signer and their answerer; staff can read them all.'
                    : 'Each thread is only between that signer and their answerer. Your organization can read them all.'}
                </p>
              </div>
              <Card>
                <CardContent className="space-y-4 pt-card">
                  {questionsQuery.isLoading ? <Loader2 className="size-4 animate-spin text-muted-foreground" /> : null}
                  {questionsQuery.isError ? (
                    <ErrorState
                      message={apiErrorMessage(questionsQuery.error, 'Could not load questions.')}
                      action={<RetryButton onClick={() => void questionsQuery.refetch()} />}
                    />
                  ) : null}
                  {questionsQuery.data?.threads.length === 0 ? (
                    <p className="text-muted-foreground">No questions yet.</p>
                  ) : null}
                  {questionsQuery.data?.threads.map((thread) => (
                    <div key={thread.id} className="space-y-2 border-t border-border pt-3 first:border-0 first:pt-0">
                      <p className="text-caption text-muted-foreground">
                        {thread.quote ? `Near “${thread.quote}”` : 'Question'}
                      </p>
                      {thread.messages.map((message) => (
                        <div key={message.id}>
                          <p className="text-caption font-medium">
                            {message.authorName}{' '}
                            <span className="font-normal text-muted-foreground">
                              {new Date(message.createdAt).toLocaleString()}
                            </span>
                          </p>
                          <p>{message.body}</p>
                        </div>
                      ))}
                    </div>
                  ))}
                </CardContent>
              </Card>
            </section>
          ) : null}

          <section className="space-y-3">
            <h2 className="text-title font-semibold">Audit trail</h2>
            {auditQuery.isError ? (
              <ErrorState
                message={apiErrorMessage(auditQuery.error, 'Could not load the audit trail.')}
                action={<RetryButton onClick={() => void auditQuery.refetch()} />}
              />
            ) : null}
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Event</TableHead>
                  <TableHead>Actor</TableHead>
                  <TableHead>When</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(auditQuery.data?.events ?? []).map((event) => (
                  <TableRow key={event.id}>
                    <TableCell className="font-mono text-caption">{event.type}</TableCell>
                    <TableCell className="text-muted-foreground">{ACTOR_LABELS[event.actorType]}</TableCell>
                    <TableCell className="whitespace-nowrap text-muted-foreground">{new Date(event.createdAt).toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </section>
        </div>

        <aside className="space-y-6">
          <DetailsCard envelope={envelope} />
          {pending && access.canAct ? (
            <Card>
              <CardHeader>
                <CardTitle>Actions</CardTitle>
                <CardDescription>{reminders.note}</CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col gap-2">
                <Button
                  variant="secondary"
                  disabled={remind.isPending || !reminders.canRemind}
                  onClick={() => remind.mutate(actionTarget)}
                >
                  {remind.isPending ? <Loader2 className="animate-spin" /> : null}
                  Remind signers
                </Button>
                <Button variant="secondary" onClick={() => setExtendOpen(true)}>
                  Extend deadline…
                </Button>
                <Button variant="destructive-quiet" onClick={() => setVoidOpen(true)}>
                  Void envelope…
                </Button>
              </CardContent>
            </Card>
          ) : null}
          {access.canDownload ? <DocumentsCard envelope={envelope} access={access} /> : null}
        </aside>
      </div>

      <ExtendDeadlineDialog
        open={extendOpen}
        onOpenChange={setExtendOpen}
        envelope={envelope}
        access={access}
      />
      <Dialog
        open={voidOpen}
        onOpenChange={(open) => {
          setVoidOpen(open)
          if (!open) voidEnvelope.reset()
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Void this envelope?</DialogTitle>
            <DialogDescription>
              Signing stops for everyone, notified signers are emailed, and the caller receives an
              envelope.voided webhook. This can&apos;t be undone.
            </DialogDescription>
          </DialogHeader>
          <Field
            label="Reason"
            htmlFor="void-reason"
            hint="Shown to signers and recorded in the audit trail."
            error={voidEnvelope.isError ? apiErrorMessage(voidEnvelope.error) : undefined}
          >
            <Input id="void-reason" value={voidReason} onChange={(event) => setVoidReason(event.target.value)} />
          </Field>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setVoidOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={voidEnvelope.isPending || voidReason.trim().length === 0}
              onClick={() => voidEnvelope.mutate({ ...actionTarget, reason: voidReason })}
            >
              {voidEnvelope.isPending ? <Loader2 className="animate-spin" /> : null}
              Void envelope
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
